import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpClientService } from 'app/core/services/http/http-client.service';
import { IBase64File } from '../../interfaces/base64-file.interface';

@Injectable()
export class FileService {
    private readonly mimeTypes: { [key: string]: string } = {
        // Document formats
        'pdf': 'application/pdf',
        'doc': 'application/msword',
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'xls': 'application/vnd.ms-excel',
        'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'ppt': 'application/vnd.ms-powerpoint',
        'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'odt': 'application/vnd.oasis.opendocument.text',
        'ods': 'application/vnd.oasis.opendocument.spreadsheet',
        'odp': 'application/vnd.oasis.opendocument.presentation',

        // Image formats
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'png': 'image/png',
        'gif': 'image/gif',
        'bmp': 'image/bmp',
        'svg': 'image/svg+xml',
        'webp': 'image/webp',

        // Audio formats
        'mp3': 'audio/mpeg',
        'wav': 'audio/wav',
        'ogg': 'audio/ogg',

        // Video formats
        'mp4': 'video/mp4',
        'avi': 'video/x-msvideo',
        'webm': 'video/webm',

        // Archive formats
        'zip': 'application/zip',
        'rar': 'application/x-rar-compressed',
        '7z': 'application/x-7z-compressed',

        // Text formats
        'txt': 'text/plain',
        'csv': 'text/csv',
        'html': 'text/html',
        'xml': 'application/xml',
        'json': 'application/json',

        // Other common formats
        'rtf': 'application/rtf',
        'snote': 'application/octet-stream',

        // Add more as needed
    };

    public constructor(private httpClientService: HttpClientService, private httpClient: HttpClient) {
    }

    public convertCsvToJson<T>(file: File): BehaviorSubject<T> {
        const jsonStream = new BehaviorSubject(null);

        const reader = new FileReader();
        reader.onload = () => {
            jsonStream.next(this.csvStringToJson((reader.result as string)));
        };

        reader.readAsText(file);

        return jsonStream;
    }

    public readFileContent(fileLocation: string): Observable<string> {
        return this.httpClient.get(fileLocation, { responseType: 'text' });
    }

    public convertFileToBase64(file: File): Observable<string> {
        return new Observable<string>((observer) => {
            const reader = new FileReader();
            reader.onload = () => {
                observer.next(reader.result as string);
                observer.complete();
            };
            reader.onerror = (error) => {
                observer.error(new Error(`FileReader error: ${ error.target?.error?.message }`));
            };
            reader.readAsDataURL(file);
        });
    }

    public convertFileListToBase64(fileList: FileList): Observable<IBase64File[]> {
        if (!fileList?.length) {
            return of([]);
        }

        const conversions = Array.from(fileList).map(file =>
            this.convertFileToBase64(file)
                .pipe(
                    map(base64 => ({
                        fileName: file.name,
                        fileType: file.type,
                        data: base64,
                    }) as IBase64File),
                    catchError(error => {
                        console.error(`Error converting file: ${ file.name }`, error);
                        return of(null);
                    }),
                ));

        return forkJoin(conversions)
            .pipe(
                map(results => results.filter((result): result is IBase64File => !!result)),
            );
    }

    public getExtensionFromFileName(fileName: string): string | null {
        const parts = fileName.split('.');
        return parts.length > 1 ? parts.pop()!.toLowerCase() : null;
    }

    public detectMimeType(b64: string): string {
        const arr = this.base64ToArrayBuffer(b64);
        const header = Array.from(arr.slice(0, 16)).map(byte => byte.toString(16).padStart(2, '0')).join('');

        const signatures: { [key: string]: string | (() => string) } = {
            // Images
            '89504e47': 'image/png',
            '47494638': 'image/gif',
            'ffd8ffe0': 'image/jpeg',
            'ffd8ffe1': 'image/jpeg',
            'ffd8ffe2': 'image/jpeg',
            'ffd8ffe3': 'image/jpeg',
            'ffd8ffe8': 'image/jpeg',
            '424d': 'image/bmp',
            '49492a00': 'image/tiff',
            '4d4d002a': 'image/tiff',
            '52494646': this.detectRIFFFormat(arr), // RIFF: WebP, WAV, AVI

            // Documents
            '25504446': 'application/pdf',
            'd0cf11e0a1b11ae1': this.detectLegacyOfficeFormat(arr), // doc/xls/ppt
            '504b0304': this.detectOfficeOpenXML(arr), // docx, xlsx, pptx
            '7b5c727466': 'application/rtf', // RTF begins with "{\rtf"
            'efbbbf': 'text/plain', // UTF-8 with BOM
            '2020': 'text/plain', // Often indicates CSV, but not definitive
            '3c3f786d6c': 'application/xml',
            '3c68746d6c': 'text/html',
            '3c21444f4354': 'text/html',

            // Text and scripts
            '2321': 'text/x-shellscript', // e.g., #!/bin/bash
            '3c3f': 'application/xml',
            '68746d6c3e': 'text/html',

            // Compressed / Archives
            // '504b0304': 'application/zip',
            '52617221': 'application/x-rar-compressed',
            '1f8b08': 'application/gzip',
            '377abcaf271c': 'application/x-7z-compressed',
            '425a68': 'application/x-bzip2',

            // Audio
            '494433': 'audio/mpeg',
            'fff15080': 'audio/mpeg',
            '4f676753': 'audio/ogg',
            '664c6143': 'audio/flac',

            // Video
            '0000001866747970': 'video/mp4',
            '1a45dfa3': 'video/webm',
        };

        for (const [magic, mime] of Object.entries(signatures)) {
            if (header.startsWith(magic)) {
                return mime as string;
            }
        }

        return 'application/octet-stream'; // Default to binary if unknown
    }

    public base64ToArrayBuffer(data: string): Uint8Array {
        // Sanitize the Base64 string
        let sanitizedBase64 = data.trim();
        sanitizedBase64 = sanitizedBase64.replace(/^["']|["']$/g, ''); // Remove surrounding quotes
        sanitizedBase64 = sanitizedBase64.replace(/\s/g, ''); // Remove line breaks and spaces

        // Validate Base64 content
        const base64Content = sanitizedBase64.split(',')[1] || sanitizedBase64; // Support prefixed or raw Base64
        const base64Regex = /^[A-Za-z0-9+/]+={0,2}$/;
        if (!base64Regex.test(base64Content)) {
            console.error('Invalid Base64 string:', base64Content);
            return new Uint8Array(0);
        }

        const binaryString = window.atob(sanitizedBase64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        return bytes;
    }

    public getMimeTypeFromExtension(extension: string): string {
        return this.mimeTypes[extension] || 'text/plain';
    }

    public getFileIcon(mimeType: string, fileName?: string): string {
        const fileIconDirectory = 'assets/icons/files/';

        let extension = this.getExtensionFromMimeType(mimeType);
        if (!extension && fileName && fileName.includes('.')) {
            extension = this.getExtensionFromFileName(fileName);
        }

        if (!extension) {
            return `${ fileIconDirectory }file-regular.svg`;
        }

        switch (extension.toLowerCase()) {
            case 'pdf':
                return `${ fileIconDirectory }file-pdf-regular.svg`;
            case 'doc':
            case 'docx':
            case 'odt':
                return `${ fileIconDirectory }file-word-regular.svg`;
            case 'csv':
                return `${ fileIconDirectory }file-csv-solid.svg`;
            case 'xls':
            case 'xlsx':
            case 'ods':
                return `${ fileIconDirectory }file-excel-regular.svg`;
            case 'ppt':
            case 'pptx':
            case 'odp':
                return `${ fileIconDirectory }file-powerpoint-regular.svg`;
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
            case 'bmp':
            case 'svg':
            case 'webp':
                return `${ fileIconDirectory }file-image-regular.svg`;
            case 'mp3':
            case 'wav':
            case 'ogg':
                return `${ fileIconDirectory }file-audio-regular.svg`;
            case 'mp4':
            case 'avi':
            case 'webm':
                return `${ fileIconDirectory }file-video-regular.svg`;
            case 'zip':
            case 'rar':
            case '7z':
                return `${ fileIconDirectory }file-zipper-regular.svg`;
            case 'txt':
            case 'rtf':
                return `${ fileIconDirectory }file-lines-regular.svg`;
            case 'html':
            case 'xml':
            case 'json':
                return `${ fileIconDirectory }file-code-regular.svg`;
            default:
                return `${ fileIconDirectory }file-regular.svg`;
        }
    }

    public getFileColor(mimeType: string): string {
        const fileColorMap: { [key: string]: string } = {
            // Document formats
            [this.mimeTypes['pdf']]: '#D32F2F', // Red for PDFs
            [this.mimeTypes['doc']]: '#1E88E5', // Blue for Word docs
            [this.mimeTypes['docx']]: '#1E88E5',
            [this.mimeTypes['xls']]: '#388E3C', // Green for Excel
            [this.mimeTypes['xlsx']]: '#388E3C',
            [this.mimeTypes['ppt']]: '#E64A19', // Orange for PowerPoint
            [this.mimeTypes['pptx']]: '#E64A19',
            [this.mimeTypes['odt']]: '#1976D2', // Blue for OpenDocument Text
            [this.mimeTypes['ods']]: '#2E7D32', // Green for OpenDocument Spreadsheet
            [this.mimeTypes['odp']]: '#D84315', // Dark Orange for OpenDocument Presentation

            // Image formats
            [this.mimeTypes['jpg']]: '#FBC02D', // Yellow for images
            [this.mimeTypes['jpeg']]: '#FBC02D',
            [this.mimeTypes['png']]: '#FBC02D',
            [this.mimeTypes['gif']]: '#FBC02D',
            [this.mimeTypes['bmp']]: '#FBC02D',
            [this.mimeTypes['svg']]: '#FBC02D',
            [this.mimeTypes['webp']]: '#FBC02D',

            // Audio formats
            [this.mimeTypes['mp3']]: '#7B1FA2', // Purple for audio
            [this.mimeTypes['wav']]: '#7B1FA2',
            [this.mimeTypes['ogg']]: '#7B1FA2',

            // Video formats
            [this.mimeTypes['mp4']]: '#0288D1', // Blue for video
            [this.mimeTypes['avi']]: '#0288D1',
            [this.mimeTypes['webm']]: '#0288D1',

            // Archive formats
            [this.mimeTypes['zip']]: '#795548', // Brown for archives
            [this.mimeTypes['rar']]: '#795548',
            [this.mimeTypes['7z']]: '#795548',

            // Text formats
            [this.mimeTypes['txt']]: '#616161', // Gray for text files
            [this.mimeTypes['csv']]: '#4CAF50', // Green for CSVs
            [this.mimeTypes['html']]: '#F57C00', // Orange for code
            [this.mimeTypes['xml']]: '#F57C00',
            [this.mimeTypes['json']]: '#F57C00',

            // Other common formats
            [this.mimeTypes['rtf']]: '#616161', // Same as TXT (gray)
            [this.mimeTypes['snote']]: '#9E9E9E', // Generic unknown type (gray)
        };

        return fileColorMap[mimeType] || '#9E9E9E'; // Default gray for unknown types
    }

    private csvStringToJson(csv: string): any[] {
        const lines = csv.split('\n');
        const result = [];
        const headers = lines[0].split(',');

        for (let i = 1; i < lines.length; i++) {
            const obj = {};
            const currentLine = lines[i].split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/g);

            // Skip defective line.
            if (!headers || !currentLine || currentLine.length !== headers.length) {
                continue;
            }

            for (let j = 0; j < headers.length; j++) {
                const key = this.removeLineBreaks(headers[j]);
                let value = this.removeLineBreaks(currentLine[j]);

                if (value) {
                    value = value.replace(/(?:^")|(?:"$)/g, '');
                }

                if (!key) {
                    continue;
                }

                obj[key] = value;
            }

            result.push(obj);
        }

        return result;
    }

    private removeLineBreaks(text: string): string {
        if (!text) {
            return null;
        }

        return text.replace(/(\r\n|\n|\r)/gm, '');
    }

    private detectRIFFFormat(arr: Uint8Array): string {
        const type = String.fromCharCode(...arr.slice(8, 12));
        switch (type) {
            case 'WAVE':
                return 'audio/wav';
            case 'AVI ':
                return 'video/avi';
            case 'WEBP':
                return 'image/webp';
            default:
                return 'application/octet-stream';
        }
    }

    private detectLegacyOfficeFormat(arr: Uint8Array): string {
        const subtype = String.fromCharCode(...arr.slice(512, 520));
        if (/Word/.test(subtype)) {
            return 'application/msword';
        }

        if (/Excel/.test(subtype)) {
            return 'application/vnd.ms-excel';
        }

        if (/Power/.test(subtype)) {
            return 'application/vnd.ms-powerpoint';
        }

        return 'application/vnd.ms-office';
    }

    private detectOfficeOpenXML(arr: Uint8Array): string {
        const asciiText = this.extractAsciiText(arr);

        if (asciiText.includes('xl/workbook.xml')) {
            return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'; // .xlsx
        } else if (asciiText.includes('word/document.xml')) {
            return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'; // .docx
        } else if (asciiText.includes('ppt/presentation.xml')) {
            return 'application/vnd.openxmlformats-officedocument.presentationml.presentation'; // .pptx
        }

        return 'application/zip'; // If no Office markers are found, it's likely a regular ZIP file
    }

    private extractAsciiText(bytes: Uint8Array): string {
        let result = '';
        for (let i = 0; i < bytes.length; i++) {
            const char = bytes[i];
            if (char >= 32 && char <= 126) {
                result += String.fromCharCode(char);
            } else {
                result += ' ';
            }
        }
        return result;
    }

    private getExtensionFromMimeType(mimeType: string): string {
        for (const [ext, mime] of Object.entries(this.mimeTypes)) {
            if (mime === mimeType) {
                return ext;
            }
        }

        return null;
    }
}
